import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { OWNER_EMAIL } from "@/lib/constants";

/**
 * Route guard for owner-only pages (currently /studio/*).
 *
 * Design:
 *   - The studio has its OWN username/password login at /studio/login that
 *     issues an HttpOnly admin JWT cookie. Every /api/admin/* endpoint
 *     independently verifies that cookie server-side. That is the REAL gate.
 *   - This client-side guard is a *convenience layer* — it hides the studio UI
 *     from non-owner Google users so they don't see an admin-looking page they
 *     can't use. It is NOT the source of truth for security.
 *
 * Rules:
 *   1. Auth context still loading  → neutral spinner (avoid a flash-redirect).
 *   2. Guest (no Google session)   → PASS THROUGH. The studio pages themselves
 *                                     will bounce to /studio/login if the admin
 *                                     JWT cookie isn't present, and the backend
 *                                     will 401 any admin API call from a guest.
 *   3. Google-signed-in as owner   → PASS THROUGH.
 *   4. Google-signed-in as anyone else → silent redirect to /. This is the only
 *                                     path we actively block, because a signed-in
 *                                     non-owner would otherwise see the shell
 *                                     of a page they cannot use.
 */
export default function OwnerOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Neutral placeholder while /auth/me is in flight — blends with the
    // obsidian canvas so it doesn't look like a broken empty page.
    return (
      <div
        data-testid="owner-guard-loading"
        className="min-h-screen bg-[#111622] flex items-center justify-center"
      >
        <div className="h-6 w-6 rounded-full border-2 border-[#26334D] border-t-[#E85A2A] animate-spin" aria-label="Checking access" />
      </div>
    );
  }

  // Only block signed-in NON-owner Google users. Guests are allowed to reach
  // the studio (the admin JWT cookie + backend checks handle real security).
  if (user && (user.email || "").trim().toLowerCase() !== OWNER_EMAIL) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return children;
}
